import { Home } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#10B981";

const problems = [
  "Intemperies = chantiers a l'arret = couts qui s'accumulent",
  "Penalites de retard non anticipees",
  "Suspensions de chantier = cauchemar administratif",
  "Couts de remobilisation (equipes, materiel) jamais comptabilises",
];

const solutions = [
  {
    title: "Simulateur penalites",
    description: "Calcul automatique selon le contrat",
  },
  {
    title: "Gestion suspensions",
    description: "Tracabilite des arrets avec motifs et couts",
  },
  {
    title: "Couts remobilisation",
    description: "Comptabilises separement",
  },
  {
    title: "Projection marge avec aleas",
    description: "\"Si 5 jours d'intemperies, ma marge sera de X%\"",
  },
];

const results = [
  {
    metric: "Penalites non anticipees",
    before: "2-3/an",
    after: "0",
  },
  {
    metric: "Delais renegocies avec client",
    before: "Rarement",
    after: "4 chantiers",
  },
  {
    metric: "Suspensions bien documentees",
    before: "Non",
    after: "100%",
  },
];

const Couvreur = () => {
  usePageMeta({
    title: "Logiciel gestion chantier couvreur — B8ild",
    description: "Suivi financier pour couvreurs et zingueurs. Rentabilité chantier toiture en temps réel. Logiciel BTP B8ild.",
    url: "https://b8ild.fr/use-cases/couvreur",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Couvreur", item: "https://b8ild.fr/use-cases/couvreur" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Logiciel gestion chantier couvreur — B8ild",
        description: "Suivi financier pour couvreurs et zingueurs. Rentabilité chantier toiture en temps réel. Logiciel BTP B8ild.",
        url: "https://b8ild.fr/use-cases/couvreur",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Couvreur-Charpentier"
        title="Marie ne laisse plus la meteo"
        subtitle="ruiner ses marges."
        description="Decouvrez comment B8ild aide les couvreurs a anticiper l'impact financier des intemperies et suspensions."
        icon={Home}
        accentColor={ACCENT_COLOR}
        personaInitials="ML"
        personaName="Marie"
        personaRole="TPE couverture-charpente, 8 personnes"
        personaCity="Rennes (Bretagne) · CA ~650 000 EUR/an"
      />

      <UseCaseContext
        context="Marie dirige une entreprise de couverture en Bretagne. Le metier est expose aux intemperies : pluie, vent, gel... Les suspensions de chantier sont frequentes, et les penalites de retard peuvent etre lourdes."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="6 mois"
      />

      <UseCaseTestimonial
        quote="L'hiver dernier, j'ai eu 3 semaines d'intemperies sur un chantier. Grace a B8ild, j'avais trace chaque jour d'arret. Le client a accepte de decaler sans penalite."
        name="Marie"
        role="Dirigeante"
        city="Rennes"
        initials="ML"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Toiture ardoise, tuile, zinc, bac acier, zinguerie, chéneau, velux, isolation sous toiture, étanchéité — vous dépendez de la météo plus que n'importe quel autre métier. Un arrêt de 3 jours pour intempéries et vos coûts explosent. B8ild trace chaque jour d'arrêt et calcule l'impact sur votre marge pour que vous puissiez négocier avec le client, chiffres en main.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : Toiture ardoise 180m² et 12 jours d'intempéries
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Laurent a un chantier de réfection de toiture en ardoise naturelle conforme DTU 40.11. Surface : 180m². Devis : 68 000€ HT. Durée prévue : 25 jours avec 3 ouvriers. Main d'œuvre : 3 × 200€/jour × 25 = 15 000€. Ardoises + zinc + bois : 32 000€. Location échafaudage : 3 500€ (forfait 5 semaines). Marge prévisionnelle : 17 500€ (25,7%).
              </p>
              <p>
                Novembre arrive. 12 jours d'intempéries sur 5 semaines. Les ouvriers ne montent pas sur le toit quand il pleut ou qu'il gèle — question de sécurité et de qualité (l'ardoise posée sur support humide ne tient pas). Le chantier passe de 25 à 37 jours effectifs. L'échafaudage ? La location continue : +2 semaines = 1 400€ de surcoût. Main d'œuvre improductive (jours d'attente) : ~4 jours × 3 × 200€ = 2 400€. Le faîtage et la zinguerie prennent aussi du retard. Marge réelle : 13 700€ (20,1%). 5,6 points de marge évaporés.
              </p>
              <p>
                Dès la 2e semaine d'intempéries, un suivi en temps réel aurait montré que le budget main d'œuvre et location partait en vrille. Réaffecter 1 ouvrier sur un chantier intérieur les jours de pluie au lieu de payer 3 gars à attendre ? Ça aurait sauvé 2 000€ de marge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Couvreurs
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment intégrer les intempéries dans un devis de couverture ?",
                  a: "Ajoutez 15 à 25% de marge de temps selon la saison. En été (mai-septembre) : +10-15%. En automne-hiver : +20-30%. Un chantier prévu sur 20 jours en novembre en prendra 26 à 30 en réalité. Chiffrez la location d'échafaudage sur la durée rallongée, pas sur la durée optimiste.",
                },
                {
                  q: "Quelles aides pour la rénovation de toiture en 2026 ?",
                  a: "MaPrimeRénov' couvre l'isolation des combles (sarking, isolation par l'extérieur) mais pas la couverture seule. Les CEE (Certificats d'Économie d'Énergie) financent l'isolation des combles perdus ou aménagés. TVA 5,5% pour les travaux d'amélioration énergétique sur un logement de plus de 2 ans. Couverture sans isolation : TVA 10%.",
                },
                {
                  q: "Quel prix au m² pour une toiture en ardoise ?",
                  a: "Ardoise naturelle posée : 100 à 180€/m² (fourniture + pose). Ardoise synthétique : 50 à 80€/m². Zinc (joint debout) : 80 à 140€/m². Tuile terre cuite : 40 à 80€/m². Ces prix incluent l'écran sous-toiture et la fixation. L'échafaudage (10 à 25€/m² de façade) et la dépose de l'ancienne couverture (15 à 30€/m²) sont en plus.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="couvreur" />

      <LandingFooter />
    </div>
  );
};

export default Couvreur;
